import { useLayoutEffect } from 'react';

import { DisplayZoom, DISPLAY_ZOOM_OPTIONS } from './types';

const DISPLAY_ZOOM_KEY = 'taytan-display-zoom';

const isDisplayZoom = (value: unknown): value is DisplayZoom =>
    DISPLAY_ZOOM_OPTIONS.includes(value as DisplayZoom);

const applyZoom = (zoom: number) => {
    // `zoom` rather than a smaller root font: it scales px as well as rem, the
    // way the browser's own zoom does. --app-vh (base.scss) undoes its effect
    // on 100vh, so full-height pages still reach the bottom of the window.
    //
    // On #root, not <html>. A pop-up is placed from its anchor's on-screen
    // rect, and a left/top set inside a zoomed element is scaled again, so at
    // 90% every menu and tooltip opened 10% nearer the top-left corner. The
    // pop-up layers MUI renders into <body> now stay unzoomed, which places
    // them exactly; base.scss scales only their contents.
    document.getElementById('root')?.style.setProperty('zoom', zoom === 1 ? '' : String(zoom));
    document.documentElement.style.setProperty('--app-zoom', String(zoom));
};

// Applied once at load, before the first paint, from the last value this
// browser saw — the saved preference arrives with a request, and without this
// every page would open at 100% and then jump.
try {
    const cached = Number(localStorage.getItem(DISPLAY_ZOOM_KEY));
    if (isDisplayZoom(cached)) applyZoom(cached);
} catch {
    // Storage blocked: the saved preference still applies once it loads.
}

/**
 * The user's display size (avatar menu), applied to the whole app. The saved
 * preference is the source of truth and follows the user across devices;
 * localStorage is only the fast cache for the next cold start, as with the
 * theme mode. Until the preferences are read, the cached value stays.
 */
export const useDisplayZoom = (displayZoom: DisplayZoom | undefined, loaded: boolean) => {
    useLayoutEffect(() => {
        if (!loaded) return;
        const zoom = isDisplayZoom(displayZoom) ? displayZoom : 1;
        applyZoom(zoom);
        try {
            localStorage.setItem(DISPLAY_ZOOM_KEY, String(zoom));
        } catch {
            // Storage blocked: nothing to cache.
        }
    }, [displayZoom, loaded]);
};
